import React from "react";
import PublicPricingHero from "../components/pricing/PublicPricingHero";
import PublicPricingPlans from "../components/pricing/PublicPricingPlans";
import PublicPricingFAQ from "../components/pricing/PublicPricingFAQ";
import ToolSeoCTA from "../components/tools-seo/ToolSeoCTA";
import SeoHead from "../components/seo/SeoHead";
import StructuredData, {
  buildFAQSchema,
  buildOrganizationSchema,
  buildSoftwareApplicationSchema
} from "../components/seo/StructuredData";
import MarketingPageShell from "../components/layout/MarketingPageShell";
import useI18n from "../i18n/useI18n";

function PublicPricingPage() {
  const { t } = useI18n();

  const tt = (key, fallback) => {
    const value = t(key);
    return value === key ? fallback : value;
  };

  const pricingFaqs = [
    {
      question: tt("pricingFaq.items.freePlan.question", "Can I start with the Free plan?"),
      answer: tt(
        "pricingFaq.items.freePlan.answer",
        "Yes. The Free plan is designed to help you try Hookora, explore the workflow, and see how the tools fit your content process before upgrading."
      )
    },
    {
      question: tt("pricingFaq.items.moveToCreator.question", "When should I move to Creator?"),
      answer: tt(
        "pricingFaq.items.moveToCreator.answer",
        "Creator is best when you are publishing more consistently and want more generations, unlimited saved hooks, and stronger workflow support."
      )
    },
    {
      question: tt("pricingFaq.items.whoIsProFor.question", "Who is Pro for?"),
      answer: tt(
        "pricingFaq.items.whoIsProFor.answer",
        "Pro is best for serious creators and power users who want higher limits, more flexibility, and deeper workflow value as Hookora continues to grow."
      )
    },
    {
      question: tt("pricingFaq.items.upgradeLater.question", "Can I upgrade later?"),
      answer: tt(
        "pricingFaq.items.upgradeLater.answer",
        "Yes. You can start free and upgrade when your content workflow needs more room."
      )
    },
    {
      question: tt(
        "pricingFaq.items.moreThanOneTool.question",
        "Does Hookora include more than one tool?"
      ),
      answer: tt(
        "pricingFaq.items.moreThanOneTool.answer",
        "Yes. Hookora includes hooks, title optimization, captions, hashtags, description rewriting, short scripts, and thumbnail text analysis."
      )
    },
    {
      question: tt(
        "pricingFaq.items.beginnerCreators.question",
        "Is Hookora built for beginner creators too?"
      ),
      answer: tt(
        "pricingFaq.items.beginnerCreators.answer",
        "Yes. Hookora is designed to feel useful whether you are just starting or already creating content regularly."
      )
    }
  ];

  return (
    <MarketingPageShell>
      <SeoHead
        title={tt("pricing.seoTitle", "Hookora Pricing | Free, Creator and Pro Plans")}
        description={tt(
          "pricing.seoDescription",
          "See Hookora pricing for Free, Creator, and Pro plans. Start free, then upgrade for more generations, saved assets, and stronger creator workflow support."
        )}
        path="/pricing"
      />

      <StructuredData id="hookora-org-schema" data={buildOrganizationSchema()} />
      <StructuredData
        id="hookora-software-schema"
        data={buildSoftwareApplicationSchema({
          description: tt(
            "pricing.softwareSchemaDescription",
            "Hookora is an AI hook workflow platform for creators with tools for hooks, titles, captions, hashtags, descriptions, scripts, and thumbnail text."
          )
        })}
      />
      <StructuredData
        id="hookora-pricing-faq-schema"
        data={buildFAQSchema(pricingFaqs)}
      />

      <PublicPricingHero />
      <PublicPricingPlans />
      <PublicPricingFAQ />

      <ToolSeoCTA
        badge={tt("pricingCta.badge", "Start free with Hookora")}
        title={tt(
          "pricingCta.title",
          "Choose a Hookora plan and build your content workflow with more clarity"
        )}
        description={tt(
          "pricingCta.description",
          "Start with the Free plan, upgrade when you need more creative room, and use Hookora to make content creation feel faster, sharper, and more consistent."
        )}
        primaryLabel={tt("pricingCta.startFree", "Create Free Account")}
        primaryTo="/register"
        secondaryLabel={tt("pricingCta.exploreFeatures", "Explore Features")}
        secondaryTo="/features"
      />
    </MarketingPageShell>
  );
}

export default PublicPricingPage;